/**
 * V2 source path: customer/midas/components/v2/MidasChatAreaV2.tsx
 * V1 source: customer/midas/components/MidasChatArea.tsx
 * Strategy: logic-copied / JSX-rewritten-in-Tailwind
 * Verified no-MUI: yes
 * Phase: 1
 *
 * Copied from V1 verbatim (logic only):
 *   - on-mount dispatches: checkBotAccess, checkHealth, fetchHistory (only
 *     when no activeThreadId + not loading), fetchThreads.
 *   - MIDAS-SCOPESTREAM: thread-buffer reads (effectiveThreadId fallback to
 *     POPUP_THREAD_ID when no active thread, popup adoption flag).
 *   - Popup adoption useEffect (append pendingBotMessage on mount).
 *   - Active-thread sync useEffect (append pendingBotMessage when stream
 *     completes on the current thread).
 *   - handleSend: clearError → setNewThreadPending if no active thread →
 *     sendMessage with originThreadId scoped.
 *   - handleSuggestionClick / handleImageClick (openArtifact dispatch).
 *   - F6.9 bot-offline empty-state branch.
 *   - max-width shrink when artifactPanelOpen.
 *
 * Visual layer rewritten in Tailwind. The chat list + composer are V2
 * components. The header is intentionally minimal (Phase 3 will own the
 * sidebar + page chrome). The bot-offline state replaces MUI's CloudOff icon
 * with a `lucide-react` `CloudOff`.
 *
 * MIDAS V2 uses the shared `useBreakpoint()` hook (768/1280 thresholds)
 * per the rollout plan default. The MIDAS-V2-Phase-1-Spec card on Paper
 * notes 769–1200 = tablet, ≥1201 = desktop — those numbers are the design
 * boundary for the dock-collapse / sidebar-rail behavior in Phase 2/3, NOT
 * an override for the chat-area composer/message responsive logic. The
 * shared hook's behavior is what the chat area cares about: phone <768,
 * tablet 768-1279, desktop ≥1280.
 */
import React, { useCallback, useEffect } from "react";
import { CloudOff } from "lucide-react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import {
  appendPendingBotMessage,
  checkBotAccess,
  checkHealth,
  clearError,
  fetchHistory,
  sendMessage,
  setNewThreadPending,
} from "../../../../shared/chatBot/chatBotSlice";
import { POPUP_THREAD_ID } from "../../../../shared/chatBot/models/chatBotModels";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { Artifact } from "../../midasPageModels";
import { fetchThreads, openArtifact } from "../../midasPageSlice";
import ChatInputAreaV2 from "./ChatInputAreaV2";
import ChatMessageListV2 from "./ChatMessageListV2";

interface MidasChatAreaV2Props {
  /** F6.7 — propagate user `prefers-reduced-motion` setting if known */
  prefersReducedMotion?: boolean;
}

const MidasChatAreaV2: React.FC<MidasChatAreaV2Props> = ({
  prefersReducedMotion = false,
}) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");
  const { isPhone } = useBreakpoint();

  const messages = useAppSelector((state) => state.chatBot.messages);
  const machineContext = useAppSelector((state) => state.chatBot.machineContext);
  const user = useAppSelector((state) => state.account.user);
  const artifactPanelOpen = useAppSelector(
    (state) => state.midasPage.artifactPanelOpen
  );
  const activeThreadId = useAppSelector(
    (state) => state.midasPage.activeThreadId
  );
  const isBotAvailable = useAppSelector(
    (state) => state.chatBot.isBotAvailable
  );
  const threadDetailLoading = useAppSelector(
    (state) => state.midasPage.threadDetailLoading
  );

  // MIDAS-SCOPESTREAM thread buffer reads
  const effectiveThreadId = activeThreadId || POPUP_THREAD_ID;
  const threadBuffer = useAppSelector(
    (state) => state.chatBot.threadBuffers[effectiveThreadId]
  );
  const popupBuffer = useAppSelector(
    (state) => state.chatBot.threadBuffers[POPUP_THREAD_ID]
  );
  const adoptingPopup =
    !activeThreadId &&
    (popupBuffer?.isLoading || popupBuffer?.pendingBotMessage);
  const isLoading = adoptingPopup
    ? popupBuffer?.isLoading || false
    : threadBuffer?.isLoading || false;

  const hasMessages = messages.length > 0;

  // Initial bot-state hydration on mount
  useEffect(() => {
    if (user) {
      dispatch(checkBotAccess());
      dispatch(checkHealth());
      if (!activeThreadId && !threadDetailLoading) {
        dispatch(fetchHistory());
      }
      dispatch(fetchThreads());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user]);

  // MIDAS-SCOPESTREAM popup adoption on mount
  useEffect(() => {
    if (popupBuffer && !popupBuffer.isLoading && popupBuffer.pendingBotMessage) {
      dispatch(appendPendingBotMessage({ threadId: POPUP_THREAD_ID }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // MIDAS-SCOPESTREAM active-thread sync on stream completion
  const hasPendingMsg = threadBuffer?.pendingBotMessage;
  useEffect(() => {
    if (
      activeThreadId &&
      threadBuffer &&
      !threadBuffer.isLoading &&
      threadBuffer.pendingBotMessage
    ) {
      dispatch(appendPendingBotMessage({ threadId: activeThreadId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThreadId, hasPendingMsg, dispatch]);

  const handleSend = useCallback(
    (message: string, image?: File) => {
      dispatch(clearError());
      if (!activeThreadId) {
        dispatch(setNewThreadPending());
      }
      dispatch(
        sendMessage({
          message,
          machineId: machineContext?.machineId,
          threadId: activeThreadId || undefined,
          image,
          originThreadId: activeThreadId || undefined,
        })
      );
    },
    [dispatch, machineContext, activeThreadId]
  );

  const handleSuggestionClick = useCallback(
    (text: string) => {
      handleSend(text);
    },
    [handleSend]
  );

  const handleImageClick = useCallback(
    (imageUrl: string, imageName: string) => {
      const artifact: Artifact = {
        type: "image",
        title: imageName,
        source: "machineid",
        url: imageUrl,
      };
      dispatch(openArtifact(artifact));
    },
    [dispatch]
  );

  // F6.9 bot-offline empty state
  const showBotOffline = isBotAvailable === false && !hasMessages && !isLoading;

  // Shrink max-width when artifact panel is open (matches V1 800px when no
  // dock; full width when dock takes the right side)
  const chatMaxWidthClass = artifactPanelOpen ? "max-w-full" : "max-w-[800px]";
  const transitionClass = prefersReducedMotion
    ? ""
    : "transition-[max-width] duration-300 ease-out";

  // Scope pill content for the composer — show machine MODEL name (Claim 4 fix).
  // Previously prioritized `internalNumber` which is a numeric ID like
  // "287104549" — that surfaced as the pill label instead of the machine name.
  // Round 17: prefer `model` (e.g. "Trens SBL 500 CNC"), prepend manufacturer
  // when present, fall back to internalNumber/SN/"Active machine" only when
  // the lookup truly fails.
  const machineName = machineContext
    ? (() => {
        const model = machineContext.model?.trim() || "";
        const brand = machineContext.manufacturerName?.trim() || "";
        if (model) return brand ? `${brand} ${model}`.trim() : model;
        if (machineContext.internalNumber) return machineContext.internalNumber;
        if (machineContext.serialNumber) return `SN ${machineContext.serialNumber}`;
        return "Active machine";
      })()
    : "";

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-midas-bone">
      {/* Bot offline */}
      {showBotOffline ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10">
          <CloudOff
            className="h-12 w-12 text-midas-muted-stone opacity-60"
            strokeWidth={1.5}
          />
          <div
            className="text-center font-sora text-[16px] font-semibold text-midas-graphite"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            {translate("midas.emptyState.botOfflineTitle")}
          </div>
          <div
            className="max-w-sm text-center font-sora text-[13px] leading-relaxed text-midas-muted-stone"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            {translate("midas.emptyState.botOfflineDesc")}
          </div>
        </div>
      ) : !hasMessages && !isLoading && !threadDetailLoading ? (
        <div className="flex flex-1 overflow-y-auto bg-midas-bone">
          {/* Phase 1 minimal welcome — full Machine Fresh Session ships in Phase 3 */}
          <div className="m-auto flex max-w-md flex-col items-center gap-4 px-6 py-12 text-center">
            <h2
              className="font-sora text-[22px] font-semibold leading-tight text-midas-graphite"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              Ask Midas anything about this machine
            </h2>
            <p
              className="font-sora text-[14px] leading-relaxed text-midas-warm-graphite"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              Hydraulic schematics, alarm codes, service history — start a
              question and Midas will pull the right citations as it goes.
            </p>
          </div>
        </div>
      ) : (
        <div
          className={`mx-auto flex w-full flex-1 flex-col overflow-hidden ${chatMaxWidthClass} ${transitionClass}`}
        >
          <ChatMessageListV2
            onSuggestionClick={handleSuggestionClick}
            isPhone={isPhone}
            onImageClick={handleImageClick}
            threadId={
              adoptingPopup
                ? POPUP_THREAD_ID
                : activeThreadId || undefined
            }
          />
        </div>
      )}

      {/* Input area */}
      <div
        className={`mx-auto w-full ${chatMaxWidthClass} ${transitionClass}`}
      >
        <ChatInputAreaV2
          onSend={handleSend}
          disabled={isLoading || isBotAvailable === false}
          imageUploadDisabled={!machineContext}
          isPhone={isPhone}
          scopeLabel={machineName || undefined}
          scopeLive={!!machineContext}
        />
      </div>
    </div>
  );
};

export default MidasChatAreaV2;
